/**
 * Delad logik för att gruppera och visa kunskapsbas-källor.
 * Används av både Kunskapsbas-sidan (full vy) och Källor-panelen i ELvis-chatten
 * (kompakt vy) så att grupperingen alltid ser likadan ut på båda ställena.
 */

export interface SourceRow {
  filename: string;
  chunk_count: number;
  department?: string | null;
  /** När källans innehåll senast indexerades (max created_at bland chunkarna).
   *  Null för en källa utan innehåll. Se migrationen 20260824_01 för varför
   *  varken created_at eller updated_at på kb_sources duger till det här. */
  last_indexed_at?: string | null;
  /** 'internal' | 'website' | 'other' — sätts vid ingest och är den beständiga
   *  sanningen om varifrån källan kommer. `isInternalSource` gissar ur
   *  filnamnet och räcker till gruppering, men inte till påståenden om hur
   *  innehållet hamnade här. */
  source_category?: string | null;
}

/** Datum i Europe/Stockholm — aldrig UTC. En indexering strax efter midnatt
 *  svensk tid ligger på föregående dygn i UTC och hade daterats en dag fel. */
export function indexedDate(iso: string): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Stockholm',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

/** Kort etikett för raden, t.ex. "24 aug". */
export function indexedDateShort(iso: string): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Stockholm',
    day: 'numeric',
    month: 'short',
  }).format(new Date(iso));
}

/**
 * Tooltip-text för när källan senast lades in.
 *
 * Ordvalet skiljer på webbkällor och uppladdade dokument: en webbsida hämtas om
 * av veckocrawlen utan att någon rört den, medan ett internt dokument bara får
 * ny text när någon laddar upp den. Att kalla båda "uppladdad" hade varit fel
 * på den ena halvan av listan.
 *
 * Typen läses ur `source_category`, som sätts vid ingest — inte ur
 * `isInternalSource`, som gissar ur filnamnet. Gissningen stämmer för dagens
 * data men bygger på en lista över kända toppdomäner: en crawlad rot-URL utan
 * sökväg ("docs.example.io") skulle klassas som uppladdat dokument. Att gruppera
 * fel i listan är en skönhetsfläck; att påstå att någon laddat upp en sida som
 * roboten hämtat är ett felaktigt påstående om vem som gjort vad.
 */
export function indexedTooltip(src: SourceRow): string | undefined {
  if (!src.last_indexed_at) return undefined;
  const datum = indexedDate(src.last_indexed_at);
  const kategori = src.source_category ?? (isInternalSource(src.filename) ? 'internal' : 'website');
  if (kategori === 'internal') return `Innehållet laddades upp ${datum}`;
  if (kategori === 'website') return `Sidan hämtades senast ${datum}`;
  return `Lades in i kunskapsbasen ${datum}`;
}

/** Föreslagna avdelningar/ämnen för interna dokument. Fri text i databasen —
 *  listan är bara snabbval; be om fler så lägger vi till dem. */
export const DEPARTMENTS = ['Ekonomi', 'Marknad', 'Information', 'Transport', 'Övrigt'] as const;
export const UNCATEGORIZED = 'Ej kategoriserad';

/** Gruppera interna dokument per avdelning i en bestämd ordning:
 *  kända avdelningar först, ev. egna därefter, "Ej kategoriserad" sist. */
export function groupByDepartment(
  items: SourceRow[],
): Array<{ dept: string; key: string; items: SourceRow[] }> {
  const map = new Map<string, SourceRow[]>();
  for (const s of items) {
    const key = s.department?.trim() || UNCATEGORIZED;
    (map.get(key) ?? map.set(key, []).get(key)!).push(s);
  }
  const known = DEPARTMENTS.filter((d) => map.has(d));
  const custom = [...map.keys()]
    .filter((k) => k !== UNCATEGORIZED && !DEPARTMENTS.includes(k as (typeof DEPARTMENTS)[number]))
    .sort((a, b) => a.localeCompare(b, 'sv'));
  const order = [...known, ...custom, ...(map.has(UNCATEGORIZED) ? [UNCATEGORIZED] : [])];
  return order.map((dept) => ({ dept, key: dept, items: map.get(dept)! }));
}

/**
 * Är källan ett uppladdat dokument? Använd den här när du har hela raden.
 *
 * `source_category` sätts vid ingest och är den beständiga sanningen;
 * filnamnsgissningen nedan är bara reserv för rader som kommer någon annanstans
 * ifrån (t.ex. källistan i ett chattsvar) och saknar kategori. Klassificeringen
 * måste vara EN — annars kan raden ritas som ett internt dokument utan
 * öppna-länk medan dess egen hovringstext säger att sidan hämtats av roboten.
 */
export function isInternalRow(src: SourceRow): boolean {
  if (src.source_category === 'internal') return true;
  if (src.source_category === 'website') return false;
  return isInternalSource(src.filename);
}

/** Internt dokument = saknar "/" och domän-markör (annars en webbkälla). */
export function isInternalSource(filename: string): boolean {
  return (
    !filename.includes('/') &&
    !filename.includes('.se') &&
    !filename.includes('.eu') &&
    !filename.includes('.com') &&
    !filename.includes('.org') &&
    !filename.includes('.net')
  );
}

/** Värd-delen av ett webb-filnamn ("www.naturvardsverket.se/...") → "www.naturvardsverket.se". */
export function hostOf(filename: string): string {
  return filename.split('/')[0];
}

/** Snyggare etikett för en domän — strippar "www.". */
export function hostLabel(host: string): string {
  return host.replace(/^www\./, '');
}

/** Kända lag/myndighets-domäner får en våg-/lag-ikon istället för glob. */
export function isLawDomain(host: string): boolean {
  return /riksdagen\.se|eur-lex|europa\.eu|lagrummet|notisum|svenskforfattningssamling/i.test(host);
}

/** Snyggt visningsnamn — interna dok strippar filändelse, webbkällor visar sökvägen. */
export function displayName(src: SourceRow): string {
  if (isInternalRow(src)) return src.filename.replace(/\.[^/.]+$/, '');
  return src.filename.split('/').slice(1).join('/').replace(/[-_]/g, ' ').trim() || src.filename;
}

/** Gruppera ett källurval i webbkällor (per domän) + interna dokument.
 *  Domänordning: el-kretsen.se först, sedan störst först, sedan alfabetiskt. */
export function groupSources(sources: SourceRow[]): {
  internal: SourceRow[];
  domains: Array<{ host: string; label: string; items: SourceRow[] }>;
} {
  const internal = sources.filter((s) => isInternalRow(s));
  const web = sources.filter((s) => !isInternalRow(s));

  const byHost = new Map<string, SourceRow[]>();
  for (const s of web) {
    const h = hostOf(s.filename);
    (byHost.get(h) ?? byHost.set(h, []).get(h)!).push(s);
  }
  const domains = [...byHost.entries()]
    .map(([host, items]) => ({ host, label: hostLabel(host), items }))
    .sort((a, b) => {
      const aEl = a.host.includes('el-kretsen.se') ? 0 : 1;
      const bEl = b.host.includes('el-kretsen.se') ? 0 : 1;
      if (aEl !== bEl) return aEl - bEl;
      if (b.items.length !== a.items.length) return b.items.length - a.items.length;
      return a.label.localeCompare(b.label, 'sv');
    });

  return { internal, domains };
}
